import React, { useState } from 'react';
import {
    ClipboardList, Users, ShoppingBag, LogOut, UserCheck,
    Calculator, Shield as ShieldIcon, LifeBuoy, Warehouse, Award, Trophy,
    UserMinus, Star, UserCog, ShoppingBasket, Ticket, MonitorCog, BarChart3,
    Truck, Radio, MoreHorizontal, TrendingUp, PhoneCall, Target, LayoutDashboard,
    MapPin, Percent, Wallet, Sprout, Smartphone, ShieldCheck
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Tooltip from '../common/Tooltip';
import { usePermissions } from '../../hooks/usePermissions';
import type { ScreenKey } from '../../config/screens';
import { SCREENS_PENDING_ACCESS_CONFIG, effectiveScreenKey } from '../../config/screens';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setMobileSidebarOpen } from '../../store/slices/uiSlice';
import type { RootState } from '../../store';

// Shared with TopNavbar.tsx — both the sidebar and header use this green now
// that the sidebar's own branding row has moved into the header.
export const PRIMARY_GREEN = '#1B4332';

interface SideNavbarProps {
    hasSession: boolean;
    adminReferralCode: string;
    onLogoutTrigger: () => void;
}

const SideNavbar: React.FC<SideNavbarProps> = ({ hasSession, adminReferralCode, onLogoutTrigger }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { isSidebarOpen, isMobileSidebarOpen } = useAppSelector((state: RootState) => state.ui);
    const { adminRole } = useAppSelector((state: RootState) => state.auth);
    const { adminProfile, departments } = useAppSelector((state: RootState) => state.users);

    // TailAdmin's hover-to-peek behavior: pinned closed still widens on mouse-over
    // (a no-op on touch, since there's no hover there). Content (labels, full
    // section names) also shows whenever the mobile drawer itself is open, even
    // if the desktop pin preference happens to be collapsed.
    const [isHovered, setIsHovered] = useState(false);
    const isExpanded = isSidebarOpen || isHovered || isMobileSidebarOpen;

    const effectiveRole = adminProfile?.role || adminRole || '';
    const isSuperAdmin = effectiveRole.toLowerCase().includes('superadmin');
    const isAdmin = effectiveRole.toLowerCase().includes('admin') && !effectiveRole.toLowerCase().includes('superadmin');
    const isEmployee = effectiveRole.toLowerCase().includes('employee') && !effectiveRole.toLowerCase().includes('admin');
    const isSupervisor = effectiveRole.toLowerCase().includes('supervisor');
    // A department head's role exactly matches that department's head_role
    // (e.g. Farm Manager for Farm Management) — StaffAccessTab locks them to
    // only their own department once inside. Role strings can be comma-joined
    // (multi-role users), so compare against each segment, not the whole string.
    const myRoles = effectiveRole.toLowerCase().split(',').map((r: string) => r.trim());
    const isDepartmentHead = departments.some((d: any) => {
        const headRole = (d?.head_role || '').trim().toLowerCase();
        return headRole !== '' && myRoles.includes(headRole);
    });
    // Staff Access is available to anyone who can manage staff: SuperAdmin, Admin, Supervisor, or a department head.
    const canManageStaff = isSuperAdmin || isAdmin || isSupervisor || isDepartmentHead;
    // Any Marketing role (Manager/Lead/Executive all contain this substring).
    const isMarketing = effectiveRole.toLowerCase().includes('marketing');
    // Only a Manager oversees the marketing roles below them — Leads and
    // Executives hold targets and earn incentives rather than review them, so
    // the overseer screens are not theirs to see. Underscore form matched too,
    // since role strings arrive both ways ('Marketing Manager' / marketing_manager).
    const isMarketingManager = /marketing[ _]manager/.test(effectiveRole.toLowerCase());
    // Farm Procurement and Farm Mgmt are SuperAdmin-only — not configurable via
    // Staff Access, and not available to plain Admin or any custom staff role.
    const canManageFarmProcurement = isSuperAdmin;

    // Per-user screen-access. Enforcement only kicks in for a configurable staff
    // member (Employee/Supervisor/Cashier) who has a non-empty screen_access map;
    // otherwise (Admin/SuperAdmin, unconfigured staff, or a backend that predates
    // the feature) we fall back to the original role-based visibility so nobody is
    // ever locked out.
    const perms = usePermissions();
    const enforce = !perms.isPrivileged && perms.hasScreenConfig;
    // `legacy` is the item's original visibility condition; used whenever
    // enforcement is inactive.
    const show = (key: ScreenKey, legacy: boolean) => (enforce ? perms.canView(key) : legacy);

    // Same gate, but for screens added AFTER a user's permissions were saved.
    //
    // A configured staff member's screen_access map contains only the keys that
    // existed when a SuperAdmin last edited it. `canView` treats an absent key
    // as 'none', so every new screen is invisible to everyone already
    // configured — including Marketing Managers and Admins, since isPrivileged
    // is SuperAdmin-only. That is a silent lockout from a screen nobody ever
    // decided to deny them.
    //
    // So an absent key falls back to the legacy role condition; an entry that
    // IS present is still obeyed exactly. This can only restore access the role
    // already implies — it never overrides an explicit denial, and it stops
    // mattering for a key the moment someone configures it.
    const showNewScreen = (key: ScreenKey, legacy: boolean) =>
        (enforce && !(SCREENS_PENDING_ACCESS_CONFIG.has(key) && !(key in perms.screenAccess)))
            ? perms.canView(key)
            : legacy;

    const currentPath = location.pathname;
    let activeTab = 'dashboard';
    if (currentPath === '/dashboard') activeTab = 'dashboard';
    else if (currentPath.includes('/analytics')) activeTab = 'analytics';
    else if (currentPath.startsWith('/orders')) activeTab = 'orders';
    else if (currentPath.includes('/roaster')) activeTab = 'roaster';
    else if (currentPath.includes('/admin-list')) activeTab = 'admin-list';
    else if (currentPath.includes('/staff-access')) activeTab = 'staff-access';
    else if (currentPath.includes('/cashiers')) activeTab = 'cashiers';
    else if (currentPath.includes('/farm-procurement')) activeTab = 'farm-procurement';
    else if (currentPath.includes('/profile')) activeTab = 'profile';
    else if (currentPath.includes('/user-management/network')) activeTab = 'network';
    else if (currentPath.includes('/emi-calculator')) activeTab = 'emi';
    else if (currentPath.includes('/acf-calculator')) activeTab = 'acf-calculator';
    else if (currentPath.includes('/acf')) activeTab = 'acf';
    else if (currentPath.startsWith('/kyc-queue')) activeTab = 'kyc-queue';
    else if (currentPath.includes('/users/customers')) {
        const fromPage = (location.state as any)?.fromPage;
        if (fromPage === 'Orders') activeTab = 'orders';
        else if (fromPage === 'Roaster') activeTab = 'roaster';
        else activeTab = 'user-management';
    }
    else if (currentPath.includes('/user-management')) activeTab = 'user-management';
    else if (currentPath.includes('/products')) activeTab = 'products';
    else if (currentPath.includes('/offer-settings')) activeTab = 'offer-settings';
    else if (currentPath.includes('/coupons')) activeTab = 'coupons';
    else if (currentPath.includes('/offers-achieved')) activeTab = 'offers-achieved';
    else if (currentPath.includes('/role-requests')) activeTab = 'role-requests';
    else if (currentPath.includes('/farm-management')) activeTab = 'farm';
    else if (currentPath.includes('/asset-values')) activeTab = 'asset-values';
    else if (currentPath.includes('/privacy-policy')) activeTab = 'privacy-policy';
    else if (currentPath.includes('/support-tickets')) activeTab = 'support-tickets';
    else if (currentPath.includes('/support')) activeTab = 'support';
    else if (currentPath.includes('/referral-landing')) activeTab = 'referral-landing';
    else if (currentPath.includes('/unit-calculator')) activeTab = 'unit-calculator';
    else if (currentPath.includes('/unit-plan-calculator')) activeTab = 'unit-plan-calculator';
    else if (currentPath.includes('/animalkart-crm')) activeTab = 'animalkart-crm';
    else if (currentPath.includes('/units-live')) activeTab = 'units-live';
    else if (currentPath.includes('/deactivate-user')) activeTab = 'deactivate-user';
    else if (currentPath.includes('/marketing/sales-report') || currentPath === '/marketing') activeTab = 'marketing-sales-report';
    else if (currentPath.includes('/marketing/leaderboard')) activeTab = 'marketing-leaderboard';
    else if (currentPath.includes('/marketing/overview')) activeTab = 'marketing-overview';
    else if (currentPath.includes('/marketing/targets')) activeTab = 'marketing-targets';
    else if (currentPath.includes('/marketing/leads')) activeTab = 'marketing-leads';
    else if (currentPath.includes('/marketing/calls')) activeTab = 'marketing-calls';
    else if (currentPath.includes('/marketing/visits')) activeTab = 'marketing-visits';
    // Checked BEFORE the plain '/marketing/incentives' test below would be
    // reached for these paths — they do not contain that substring, but
    // keeping them adjacent makes the ordering obvious to the next editor.
    else if (currentPath.includes('/marketing/farm-incentives')) activeTab = 'marketing-farm-incentives';
    else if (currentPath.includes('/marketing/farm-leaderboard')) activeTab = 'marketing-farm-leaderboard';
    else if (currentPath.includes('/marketing/incentives')) activeTab = 'marketing-incentives';
    else if (currentPath.includes('/marketing/my-orders')) activeTab = 'marketing-orders';
    else if (currentPath.includes('/marketing/farm-orders')) activeTab = 'marketing-farm-orders';

    // Plain uppercase label above a group — never a dropdown, just a heading so the
    // sidebar reads as sections (TailAdmin's "MENU / SUPPORT / OTHERS" grouping
    // style). Collapsed to TailAdmin's own horizontal-dots glyph when the sidebar
    // itself is collapsed, since there's no room for the label there.
    // The parent <ul>'s gap-[6px] is deliberately tight (item-to-item rhythm,
    // not section rhythm) to keep the whole list's height down with this many
    // groups; an extra mt-[10px] here (skipped on the very first header) is what
    // actually separates one section from the next.
    const sectionHeader = (label: string) => (
        <li aria-hidden={!isExpanded} className="mt-[10px] first:mt-0">
            {isExpanded ? (
                <p className="font-medium uppercase text-white/40 text-theme-xs">{label}</p>
            ) : (
                <div className="flex justify-center text-white/30">
                    <MoreHorizontal size={24} />
                </div>
            )}
        </li>
    );

    const navItem = (tab: string, icon: React.ReactNode, label: string, onClick: () => void) => {
        const isActive = activeTab === tab;
        return (
            <li>
                <Tooltip content={label} disabled={isExpanded}>
                    <button
                        onClick={onClick}
                        className={`menu-item group outline-none focus:outline-none focus-visible:outline-none focus:ring-0 ${isActive ? 'menu-item-active' : 'menu-item-inactive'} ${!isExpanded ? 'justify-center' : ''}`}
                    >
                        <span className={`menu-item-icon-size ${isActive ? 'menu-item-icon-active' : 'menu-item-icon-inactive'}`}>
                            {icon}
                        </span>
                        {isExpanded && (
                            <span className="whitespace-nowrap overflow-hidden text-ellipsis text-left">{label}</span>
                        )}
                    </button>
                </Tooltip>
            </li>
        );
    };

    return (
        <>
            {/* Mobile overlay — matches TailAdmin's own breakpoint (lg, 1024px): below
                that it's a full slide-in drawer over a dimmed backdrop, not an
                always-visible narrow rail. Driven by isMobileSidebarOpen, not
                isSidebarOpen — see that state's own comment for why they're separate. */}
            <div
                className={`fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[99] lg:hidden transition-opacity duration-300 ${isMobileSidebarOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                onClick={() => dispatch(setMobileSidebarOpen(false))}
            />

            <nav
                className={`
                    absolute lg:static inset-y-0 left-0
                    ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
                    w-[var(--sidebar-width)] ${isExpanded ? 'lg:w-[var(--sidebar-width)]' : 'lg:w-[var(--sidebar-collapsed-width)]'}
                    flex flex-col shrink-0
                    transition-transform lg:transition-[width] duration-[var(--transition-speed)] ease-[var(--ease-in-out)]
                    z-[100] h-full
                `}
                style={{ backgroundColor: PRIMARY_GREEN }}
                onMouseEnter={() => !isSidebarOpen && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >


                {/* Scrollable nav list */}
                <div className="flex-1 overflow-y-auto px-[20px] py-[12px] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden flex flex-col">
                    <ul className="list-none flex flex-col w-full gap-[6px]">
                        {/* Overview */}
                        {hasSession && (isSuperAdmin || isAdmin) && (
                            <>
                                {sectionHeader('Overview')}
                                {navItem('analytics', <BarChart3 size={19} />, 'Analytics', () => navigate('/analytics'))}
                            </>
                        )}

                        {/* Orders — placing/fulfilling/procuring for customers */}
                        {sectionHeader('Orders')}
                        {hasSession && show('orders', true) && navItem('orders', <ClipboardList size={19} />, 'Orders', () => navigate('/orders'))}
                        {hasSession && show('roaster', !isEmployee) && navItem('roaster', <ShoppingBasket size={19} />, 'Roaster', () => navigate('/roaster'))}
                        {hasSession && canManageFarmProcurement && navItem('farm-procurement', <Truck size={19} />, 'Farm Procurement', () => navigate('/farm-procurement'))}

                        {/* Users — accounts, staff, and their roles/permissions */}
                        {sectionHeader('Users')}
                        {hasSession && show('users', true) && navItem('user-management', <Users size={19} />, 'Users', () => navigate('/user-management'))}
                        {/* Investor KYC / bank / unit-nominee verification queue — its own screen;
                            an access map saved before it existed falls back to Users */}
                        {hasSession && show(effectiveScreenKey(perms.screenAccess, 'kyc_queue') as ScreenKey, true) && navItem('kyc-queue', <ShieldCheck size={19} />, 'KYC Queue', () => navigate('/kyc-queue'))}
                        {hasSession && show('network', !isEmployee) && navItem('network', <Users size={19} />, 'Network', () => navigate('/user-management/network'))}
                        {hasSession && isSuperAdmin && navItem('admin-list', <UserCheck size={19} />, 'Admins', () => navigate('/admin-list'))}
                        {hasSession && canManageStaff && navItem('staff-access', <MonitorCog size={19} />, 'Staff Mgmt', () => navigate('/staff-access'))}
                        {/* The cashier roster. showNewScreen, not show: the key
                            did not exist when anyone's access map was saved, so
                            an absent entry falls back to the role instead of
                            hiding the screen from every configured staff member.
                            Legacy condition is Admin/SuperAdmin -- they maintain
                            the list; other staff get the cashier dropdown on the
                            payment form without needing this screen, and a
                            SuperAdmin can grant it explicitly in Staff Mgmt. */}
                        {hasSession && showNewScreen('cashiers', isSuperAdmin || isAdmin) && navItem('cashiers', <Wallet size={19} />, 'Cashiers', () => navigate('/cashiers'))}
                        {hasSession && show('role_requests', !isEmployee) && navItem('role-requests', <UserCog size={19} />, 'Role Requests', () => navigate('/role-requests'))}

                        {/* Products — catalog and farm stock */}
                        {sectionHeader('Products')}
                        {hasSession && show('products', true) && navItem('products', <ShoppingBag size={19} />, 'Products', () => navigate('/products'))}
                        {hasSession && isSuperAdmin && navItem('farm', <Warehouse size={19} />, 'Farm Mgmt', () => navigate('/farm-management'))}

                        {/* Marketing — sales report, leaderboard and marketing orders */}
                        {hasSession && (isMarketing || isAdmin || isSuperAdmin) && (
                            <>
                                {sectionHeader('Marketing')}
                                {/* Overview sits first: every marketing role gets it,
                                    each seeing their own slice of the hierarchy. */}
                                {showNewScreen('marketing_overview', true) && navItem('marketing-overview', <LayoutDashboard size={19} />, 'Overview', () => navigate('/marketing/overview'))}
                                {/* Sales Report and Leader Board used to render unconditionally.
                                    Now that both are grantable in Staff Access, an explicit
                                    denial has to actually hide them — otherwise the tick box
                                    lies. showNewScreen, not show, so nobody already configured
                                    loses a screen that was never deniable before. */}
                                {showNewScreen('marketing_sales_report', true) && navItem('marketing-sales-report', <TrendingUp size={19} />, 'Sales Report', () => navigate('/marketing/sales-report'))}
                                {showNewScreen('marketing_leaderboard', true) && navItem('marketing-leaderboard', <Trophy size={19} />, 'Leader Board', () => navigate('/marketing/leaderboard'))}
                                {/* Admin/SuperAdmin get the orders list too — they approve
                                    what is on it, so hiding it from them made the queue
                                    unreviewable. The label follows the scope: an Admin sees
                                    every executive's orders, not "my" orders. */}
                                {(isMarketing || isAdmin || isSuperAdmin) && showNewScreen('marketing_orders', true) && navItem('marketing-orders', <ClipboardList size={19} />, (isAdmin || isSuperAdmin) ? 'Marketing Orders' : 'My Orders', () => navigate('/marketing/my-orders'))}
                                {/* Paid orders whose lead had a farm visit, with the farm
                                    executive's OWN incentive approval on each — separate
                                    from the marketing incentive approved just above (see
                                    routers.marketing.approve_farm_incentive). */}
                                {(isMarketing || isAdmin || isSuperAdmin) && showNewScreen('marketing_farm_orders', true) && navItem('marketing-farm-orders', <Sprout size={19} />, 'Farm Orders', () => navigate('/marketing/farm-orders'))}
                                {/* Leads overview: all statuses, employee-wise. Admin and
                                    SuperAdmin see every lead; marketing roles keep their
                                    own scope — the API decides, not this component. */}
                                {(isMarketing || isAdmin || isSuperAdmin) && showNewScreen('marketing_leads', true) && navItem('marketing-leads', <Users size={19} />, 'Leads', () => navigate('/marketing/leads'))}
                                {/* Monthly target achievement per person. The rollup is
                                    returned to whoever oversees targets — Admin/SuperAdmin
                                    org-wide, a Marketing Manager over the team they assign
                                    to — so the nav entry matches who can actually use the
                                    screen. Leads and Executives hold targets rather than
                                    oversee them and get no rollup, hence no entry. */}
                                {(isMarketingManager || isAdmin || isSuperAdmin) && showNewScreen('marketing_targets', true) && navItem('marketing-targets', <Target size={19} />, 'Targets', () => navigate('/marketing/targets'))}
                                {/* Creating a plan (with its slabs) is Marketing Manager-only,
                                    but the list itself is open to every marketing role — the
                                    API scopes what a Lead/Exec sees to ACTIVE plans only. */}
                                {(isMarketing || isAdmin || isSuperAdmin) && showNewScreen('marketing_incentives', true) && navItem('marketing-incentives', <Percent size={19} />, 'Incentive Plans', () => navigate('/marketing/incentives'))}
                                {/* Farm staff incentives: their own progressive ladder and
                                    board, separate from the marketing ones above because the
                                    two are paid under different rules. */}
                                {(isMarketing || isAdmin || isSuperAdmin) && showNewScreen('marketing_farm_incentives', true) && navItem('marketing-farm-incentives', <Sprout size={19} />, 'Farm Incentives', () => navigate('/marketing/farm-incentives'))}
                                {(isMarketing || isAdmin || isSuperAdmin) && showNewScreen('marketing_farm_leaderboard', true) && navItem('marketing-farm-leaderboard', <Trophy size={19} />, 'Farm Leaderboard', () => navigate('/marketing/farm-leaderboard'))}
                                {(isMarketing || isAdmin || isSuperAdmin) && showNewScreen('marketing_calls', true) && navItem('marketing-calls', <PhoneCall size={19} />, 'Call Log', () => navigate('/marketing/calls'))}
                                {(isMarketing || isAdmin || isSuperAdmin) && showNewScreen('marketing_visits', true) && navItem('marketing-visits', <MapPin size={19} />, 'Visits', () => navigate('/marketing/visits'))}
                            </>
                        )}

                        {/* Offers — incentives and coupons */}
                        {sectionHeader('Offers')}
                        {hasSession && show('coupons', isSuperAdmin || isAdmin || isEmployee) && navItem('coupons', <Ticket size={19} />, 'Coupon Mgmt', () => navigate('/coupons'))}
                        {hasSession && show('offer_settings', !isEmployee) && navItem('offer-settings', <Award size={19} />, 'Offers Mgmt', () => navigate('/offer-settings'))}
                        {hasSession && show('offers_achieved', !isEmployee) && navItem('offers-achieved', <Trophy size={19} />, 'Offers Earned', () => navigate('/offers-achieved'))}

                        {/* Others — grouped together per request: Live Count, Asset Values, Referral Page */}
                        {sectionHeader('Others')}
                        {hasSession && navItem('units-live', <Radio size={19} />, 'Live Count', () => navigate('/units-live', { state: { fromDashboard: true } }))}
                        {hasSession && show('asset_values', isSuperAdmin) && navItem('asset-values', <Calculator size={19} />, 'Asset Values', () => navigate('/asset-values'))}
                        {hasSession && navItem('referral-landing', <Star size={19} />, 'Referral Page', () => navigate('/referral-landing', { state: { adminReferralCode, fromDashboard: true } }))}

                        {/* Calculators */}
                        {sectionHeader('Calculators')}
                        {navItem('emi', <Calculator size={19} />, 'EMI Calculator', () => navigate('/emi-calculator', { state: { fromDashboard: true } }))}
                        {navItem('acf-calculator', <Calculator size={19} />, 'ACF Calculator', () => navigate('/acf-calculator', { state: { fromDashboard: true } }))}
                        {navItem('unit-calculator', <Calculator size={19} />, 'Unit Calculator', () => navigate('/unit-calculator', { state: { fromDashboard: true } }))}
                        {navItem('unit-plan-calculator', <Calculator size={19} />, 'Unit Plan Calculator', () => navigate('/unit-plan-calculator', { state: { fromDashboard: true } }))}
                        {navItem('animalkart-crm', <Smartphone size={19} />, 'AnimalKart CRM', () => navigate('/animalkart-crm', { state: { fromDashboard: true } }))}

                        {/* Support — account and help */}
                        {sectionHeader('Support')}
                        {hasSession && show('deactivate_user', !isEmployee) && navItem('deactivate-user', <UserMinus size={19} />, 'Deactivate User', () => navigate('/deactivate-user', { state: { fromDashboard: true } }))}
                        {navItem('privacy-policy', <ShieldIcon size={19} />, 'Privacy & Policy', () => navigate('/privacy-policy', { state: { fromDashboard: true } }))}
                        {navItem('support', <LifeBuoy size={19} />, 'Help & Support', () => navigate('/support', { state: { fromDashboard: true } }))}
                    </ul>

                    {/* Logout */}
                    {hasSession && (
                        <div className="mt-auto pt-[12px] border-t border-white/10">
                            <Tooltip content="Logout" disabled={isExpanded}>
                                <button
                                    onClick={onLogoutTrigger}
                                    className={`menu-item menu-item-inactive hover:!text-red-400 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 ${!isExpanded ? 'justify-center' : ''}`}
                                >
                                    <span className="menu-item-icon-size">
                                        <LogOut size={19} className="shrink-0" />
                                    </span>
                                    {isExpanded && <span>Logout</span>}
                                </button>
                            </Tooltip>
                        </div>
                    )}
                </div>
            </nav>
        </>
    );
};

export default SideNavbar;
